/**
 * Text Clamp Manager
 * Handles expandable one-line/multi-line text clamping.
 */

var TextClampManager = (function() {
  'use strict';

  var initialized = false;
  var resizeTimer = null;
  var _handleClick = null;
  var _handleResize = null;

  function clampContent(container) {
    return container ? container.querySelector('[data-text-clamp-content]') : null;
  }

  function clampToggle(container) {
    return container ? container.querySelector('[data-text-clamp-toggle]') : null;
  }

  function isExpanded(container) {
    return container && container.getAttribute('data-text-clamp-expanded') === 'true';
  }

  function setExpanded(container, expanded) {
    var content = clampContent(container);
    var toggle = clampToggle(container);
    var lines = container ? container.getAttribute('data-text-clamp-lines') : null;

    if (!content || !toggle) return;

    container.setAttribute('data-text-clamp-expanded', expanded ? 'true' : 'false');
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.textContent = expanded ? 'See less' : 'See more';

    if (expanded) {
      content.style.removeProperty('display');
      content.style.removeProperty('-webkit-box-orient');
      content.style.removeProperty('-webkit-line-clamp');
      content.style.removeProperty('overflow');
    } else {
      content.style.setProperty('display', '-webkit-box');
      content.style.setProperty('-webkit-box-orient', 'vertical');
      content.style.setProperty('-webkit-line-clamp', lines || '1');
      content.style.setProperty('overflow', 'hidden');
    }
  }

  function isOverflowing(content) {
    return content.scrollHeight > content.clientHeight + 1;
  }

  function refreshContainer(container) {
    var content = clampContent(container);
    var toggle = clampToggle(container);
    var lines = container ? container.getAttribute('data-text-clamp-lines') : null;

    if (!content || !toggle) return;

    if (isExpanded(container)) {
      toggle.classList.remove('hidden');
      toggle.textContent = 'See less';
      toggle.setAttribute('aria-expanded', 'true');
      content.style.removeProperty('display');
      content.style.removeProperty('-webkit-box-orient');
      content.style.removeProperty('-webkit-line-clamp');
      content.style.removeProperty('overflow');
      return;
    }

    content.style.setProperty('display', '-webkit-box');
    content.style.setProperty('-webkit-box-orient', 'vertical');
    content.style.setProperty('-webkit-line-clamp', lines || '1');
    content.style.setProperty('overflow', 'hidden');

    if (isOverflowing(content)) {
      toggle.classList.remove('hidden');
      toggle.textContent = 'See more';
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      toggle.classList.add('hidden');
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  function refreshAll() {
    document.querySelectorAll('[data-text-clamp]').forEach(function(container) {
      refreshContainer(container);
    });
  }

  function handleClick(event) {
    var toggle = event.target.closest('[data-text-clamp-toggle]');
    if (!toggle) return;

    var container = toggle.closest('[data-text-clamp]');
    if (!container) return;

    var expanded = isExpanded(container);
    setExpanded(container, !expanded);
  }

  function handleResize() {
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }

    resizeTimer = setTimeout(function() {
      refreshAll();
    }, 100);
  }

  function init() {
    if (initialized) return;

    _handleClick = handleClick;
    _handleResize = handleResize;

    document.addEventListener('click', _handleClick);
    window.addEventListener('resize', _handleResize);
    document.addEventListener('turbolinks:load', refreshAll);
    document.addEventListener('turbo:load', refreshAll);
    document.addEventListener('page:load', refreshAll);
    document.addEventListener('DOMContentLoaded', refreshAll);

    initialized = true;
    refreshAll();
  }

  function reset() {
    if (_handleClick) {
      document.removeEventListener('click', _handleClick);
      _handleClick = null;
    }
    if (_handleResize) {
      window.removeEventListener('resize', _handleResize);
      _handleResize = null;
    }
    document.removeEventListener('turbolinks:load', refreshAll);
    document.removeEventListener('turbo:load', refreshAll);
    document.removeEventListener('page:load', refreshAll);
    document.removeEventListener('DOMContentLoaded', refreshAll);

    if (resizeTimer) {
      clearTimeout(resizeTimer);
      resizeTimer = null;
    }

    initialized = false;
  }

  return {
    init: init,
    reset: reset,
    refreshAll: refreshAll
  };
})();

document.addEventListener('turbo:before-cache', function() {
  TextClampManager.reset();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', TextClampManager.init);
} else {
  TextClampManager.init();
}

window.TextClampManager = TextClampManager;
